import React, { useState, useEffect } from 'react';
import { captureException } from '@sentry/react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DateTime } from 'luxon';
import { useForm } from '@tanstack/react-form';
import { useCompany } from '../../stores';
import { importProducts, getImportJobStatus, getImportJobsList } from '../../api/company';
import { Product } from '../../model/Product';
import { Money } from '../../utils/currency';
import Timestamp from '../../model/Timestamp';
import { timestampToDate } from '../../utils/date';

const ROWS_PER_PAGE = 15;

interface DryrunResult {
  articles: Product[];
  dryRun: boolean;
  invalidArticles: string[];
  newBookkeepingAccounts: string[];
  newCategories: string[];
  valid: boolean;
  newArticlesCount: number;
  updateArticlesCount: number;
}

interface ImportStatus {
  companyId: string;
  status: string;
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
  error?: string;
}

const AVAILABLE_FIELDS = [
  { value: 'name', label: 'Namn' },
  { value: 'articleNumber', label: 'Artikelnummer' },
  { value: 'price', label: 'Pris' },
  { value: 'category', label: 'Kategori' },
  { value: 'ean', label: 'EAN' },
  { value: 'tax', label: 'Moms' },
  { value: 'id', label: 'Enklakassan ID' },
  { value: 'account', label: 'Bokföringskonto' },
  { value: 'simpleStock', label: 'Lagersaldo' },
  { value: 'information', label: 'Informationsfält' },
  { value: 'undefined', label: 'Ignorera' },
] as const;

const SEPARATORS = [
  { value: ',', label: 'Komma (,)' },
  { value: ';', label: 'Semikolon (;)' },
] as const;

const ENCODINGS = [
  { value: 'latin1', label: 'Latin-1' },
  { value: 'utf8', label: 'UTF-8' },
] as const;

type AvailableField = (typeof AVAILABLE_FIELDS)[number]['value'];
type Separator = (typeof SEPARATORS)[number]['value'];
type Encoding = (typeof ENCODINGS)[number]['value'];

interface ColumnMapping {
  field: AvailableField;
}

interface FormValues {
  file: File | null;
  showInShop: boolean;
  separator: Separator;
  encoding: Encoding;
  columns: ColumnMapping[];
}

// Add storage key helper
const getStorageKey = (companyKey: string) => `productImport_settings_${companyKey}`;

// Add type for stored settings
interface StoredSettings {
  columns: ColumnMapping[];
  separator: Separator;
  encoding: Encoding;
  showInShop: boolean;
}

const ProductImport = () => {
  const companyStore = useCompany();
  const company = companyStore.data;
  const [validationResult, setValidationResult] = useState<DryrunResult | null>(null);
  const [page, setPage] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importCompleted, setImportCompleted] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);
  const [importJobs, setImportJobs] = useState<ImportStatus[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      file: null,
      showInShop: false,
      separator: ',',
      encoding: 'latin1',
      columns: [
        { field: 'name' },
        { field: 'articleNumber' },
        { field: 'price' },
        { field: 'category' },
        { field: 'tax' },
      ],
    },
    onSubmit: async ({ value }) => {
      setIsValidating(true);
      setValidationResult(null);

      try {
        const { companyKey } = companyStore;
        const { file } = value;
        if (!companyKey || !file) {
          throw new Error('A company and file are required to validate products.');
        }

        // Save current settings before validation
        if (companyStore.companyKey) {
          const settingsToSave: StoredSettings = {
            columns: value.columns,
            separator: value.separator,
            encoding: value.encoding,
            showInShop: value.showInShop,
          };
          localStorage.setItem(
            getStorageKey(companyStore.companyKey),
            JSON.stringify(settingsToSave),
          );
        }

        const headerString = value.columns.map((mapping) => mapping.field).join(',');

        const data = await importProducts(
          companyKey,
          file,
          headerString,
          true,
          value.showInShop,
          value.separator,
          value.encoding,
        );

        setValidationResult(data);
      } catch (error) {
        captureException(error);
      } finally {
        setIsValidating(false);
      }
    },
  });

  const fetchImportJobs = () => {
    if (!companyStore.companyKey) {
      return;
    }
    setIsLoadingJobs(true);
    getImportJobsList(companyStore.companyKey)
      .then((jobs) => {
        setImportJobs(jobs);
      })
      .catch((error) => {
        captureException(error);
      })
      .finally(() => {
        setIsLoadingJobs(false);
      });
  };

  useEffect(() => {
    if (companyStore.companyKey) {
      const savedSettings = localStorage.getItem(getStorageKey(companyStore.companyKey));
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings) as StoredSettings;
          form.update({
            defaultValues: {
              columns: parsed.columns,
              separator: parsed.separator,
              encoding: parsed.encoding,
              showInShop: parsed.showInShop,
              file: null,
            },
          });
        } catch (error) {
          captureException(error);
        }
      }
    }
  }, [companyStore.companyKey]);

  useEffect(() => {
    if (companyStore.companyKey) {
      fetchImportJobs();
    }
  }, [companyStore.companyKey]);

  useEffect(() => {
    const { companyKey } = companyStore;
    if (!importJobId || !companyKey) {
      return undefined;
    }

    const pollStatus = () => {
      getImportJobStatus(companyKey, importJobId)
        .then((status) => {
          setImportStatus(status);
          if (status.status === 'completed' || status.status === 'failed') {
            setImportCompleted(true);
            setIsImporting(false);
          }
        })
        .catch((error) => {
          captureException(error);
        });
    };

    pollStatus();
    const intervalId = setInterval(pollStatus, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [importJobId, companyStore.companyKey]);

  if (!company) {
    return null;
  }

  const formatTimestamp = (timestamp: Timestamp | undefined): string => {
    if (!timestamp) {
      return '-';
    }
    const date = timestampToDate(timestamp);
    if (!date) {
      return '-';
    }
    return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd HH:mm');
  };

  const getStatusColor = (status: string) => {
    if (status === 'completed') {
      return 'success';
    }
    if (status === 'failed') {
      return 'error';
    }
    if (status === 'IN_PROGRESS' || status === 'PENDING') {
      return 'warning';
    }
    return 'default';
  };

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        Produkt import
      </Typography>
      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '1rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Import Jobs</Typography>
          <IconButton onClick={fetchImportJobs} disabled={isLoadingJobs}>
            {isLoadingJobs ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Box>
        {isLoadingJobs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Completed At</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {importJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No import jobs found
                  </TableCell>
                </TableRow>
              ) : (
                importJobs.map((job, index) => {
                  const date = timestampToDate(job.createdAt);
                  const keyValue = date ? date.getTime() : index;
                  return (
                    <TableRow key={`import-job-${keyValue}`}>
                      <TableCell>
                        <Typography variant="body2" color={getStatusColor(job.status)}>
                          {job.status}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatTimestamp(job.createdAt)}</TableCell>
                      <TableCell>
                        {job.totalTasks > 0
                          ? `${job.completedTasks} / ${job.totalTasks} (${Math.round(
                              (job.completedTasks / job.totalTasks) * 100,
                            )}%)`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{formatTimestamp(job.completedAt)}</TableCell>
                      <TableCell>
                        {job.error ? (
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={job.error}
                            color="error"
                          >
                            {job.error}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '1rem' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                CSV Kolumner
              </Typography>
              <form.Field name="columns">
                {(field) => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {field.state.value.map((_, index) => (
                      <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={`column-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Kolumn {index + 1}</InputLabel>
                          <Select
                            value={field.state.value[index].field}
                            onChange={(e) => {
                              const newColumns = [...field.state.value];
                              newColumns[index] = { field: e.target.value as AvailableField };
                              field.handleChange(newColumns);
                            }}
                            label={`Kolumn ${index + 1}`}
                          >
                            {AVAILABLE_FIELDS.map((fieldOption) => (
                              <MenuItem key={fieldOption.value} value={fieldOption.value}>
                                {fieldOption.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          onClick={() => {
                            const newColumns = [...field.state.value];
                            newColumns.splice(index, 1);
                            field.handleChange(newColumns);
                          }}
                          color="error"
                          sx={{ flexShrink: 0 }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => {
                        field.handleChange([...field.state.value, { field: 'undefined' }]);
                      }}
                      variant="contained"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Lägg till kolumn
                    </Button>
                  </Box>
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12}>
              <form.Field name="separator">
                {(field) => (
                  <FormControl fullWidth>
                    <InputLabel>Separator</InputLabel>
                    <Select
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value as Separator)}
                      label="Separator"
                    >
                      {SEPARATORS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12}>
              <form.Field name="encoding">
                {(field) => (
                  <FormControl fullWidth>
                    <InputLabel>Teckenkodning</InputLabel>
                    <Select
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value as Encoding)}
                      label="Teckenkodning"
                    >
                      {ENCODINGS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12}>
              <form.Field name="showInShop">
                {(field) => (
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Show in shop"
                    checked={field.state.value}
                    onChange={(e) => field.handleChange((e.target as HTMLInputElement).checked)}
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12}>
              <form.Field
                name="file"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) {
                      return 'Välj en fil att importera';
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <input
                    type="file"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        field.handleChange(e.target.files[0]);
                      }
                    }}
                  />
                )}
              </form.Field>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            type="submit"
            sx={{ mt: 2 }}
            disabled={isValidating}
            startIcon={isValidating ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isValidating ? 'Validerar...' : 'Validera'}
          </Button>
        </form>

        {!isValidating && validationResult && (
          <>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Validerings resultat
              </Typography>
              <p>Status: {validationResult.valid ? 'Valid' : 'Invalid'}</p>
              <p>Nya artiklar: {validationResult.newArticlesCount}</p>
              <p>Uppdaterade artiklar: {validationResult.updateArticlesCount}</p>
              {!validationResult.valid && (
                <p>Invalid articles: {validationResult.invalidArticles?.join(',').slice(0, 50)}</p>
              )}
              <p>
                New categories: ({validationResult.newCategories.length})
                {validationResult.newCategories?.join(',').slice(0, 50)}
              </p>
              <p>
                New bookkeeping accounts:{' '}
                {validationResult.newBookkeepingAccounts?.join(',').slice(0, 50)}
              </p>
            </Box>

            <Box sx={{ flexDirection: 'row', display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <Box sx={{ flexDirection: 'row', display: 'flex' }}>
                <TablePagination
                  count={validationResult.articles.length}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  component={Box}
                  rowsPerPage={ROWS_PER_PAGE}
                  page={page}
                  onPageChange={(event, newPage) => {
                    setPage(newPage);
                  }}
                />
              </Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Namn</TableCell>
                  <TableCell>Artikelnummer</TableCell>
                  <TableCell>ean</TableCell>
                  <TableCell>Pris</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Moms</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validationResult.articles
                  .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((article, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={`article-${article.articleNumber || ''}-${index}`}>
                      <TableCell>{article.title}</TableCell>
                      <TableCell>{article.articleNumber}</TableCell>
                      <TableCell>{article.ean?.code}</TableCell>
                      <TableCell>
                        {new Money(article.price).format()}{' '}
                        {article.priceType === 'rörligt' ? '(Öppet pris)' : ''}
                      </TableCell>
                      <TableCell>{article.category}</TableCell>
                      <TableCell>{article.tax}%</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {validationResult.valid && (
              <>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setIsImporting(true);
                    setImportCompleted(false);
                    setImportStatus(null);
                    setImportJobId(null);
                    try {
                      const { companyKey } = companyStore;
                      const { file } = form.state.values;
                      if (!companyKey || !file) {
                        throw new Error('A company and file are required to import products.');
                      }

                      const headerString = form.state.values.columns
                        .map((mapping) => mapping.field)
                        .join(',');

                      const result = await importProducts(
                        companyKey,
                        file,
                        headerString,
                        false,
                        form.state.values.showInShop,
                        form.state.values.separator,
                        form.state.values.encoding,
                      );
                      if (result.importJobId) {
                        setImportJobId(result.importJobId);
                      } else {
                        setImportCompleted(true);
                        setIsImporting(false);
                      }
                    } catch (error) {
                      captureException(error);
                      setIsImporting(false);
                    }
                  }}
                  disabled={isImporting}
                  startIcon={
                    isImporting ? <CircularProgress size={20} color="inherit" /> : undefined
                  }
                >
                  {isImporting ? 'Importerar...' : 'Importera'}
                </Button>
                {importStatus && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1">Status: {importStatus.status}</Typography>
                    {importStatus.totalTasks > 0 && (
                      <Typography variant="body2">
                        Framsteg: {importStatus.completedTasks} / {importStatus.totalTasks} (
                        {Math.round((importStatus.completedTasks / importStatus.totalTasks) * 100)}
                        %)
                      </Typography>
                    )}
                    {importStatus.failedTasks > 0 && (
                      <Typography variant="body2" color="error">
                        Misslyckade: {importStatus.failedTasks}
                      </Typography>
                    )}
                    {importStatus.error && (
                      <Typography variant="body2" color="error">
                        Fel: {importStatus.error}
                      </Typography>
                    )}
                  </Box>
                )}
                {importCompleted && importStatus && (
                  <Typography
                    variant="body1"
                    sx={{ mt: 2 }}
                    color={importStatus.status === 'FAILED' ? 'error' : 'success'}
                  >
                    {importStatus.status === 'FAILED'
                      ? 'Import misslyckades'
                      : 'Import completed successfully'}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProductImport;
